import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const BASE_URL = "https://www.biz360compliance.com";
export const DEFAULT_IMAGE = `${BASE_URL}/logo.png`;
export const DEFAULT_TITLE = "Biz360 Compliance | Business Registration, GST, Tax & ROC Compliance in India & Maharashtra";
export const DEFAULT_DESC = "Expert-led business registration, GST filing, ROC compliance, accounting & corporate secretarial services across India with priority focus in Pune, Mumbai, Parbhani & Maharashtra.";

/**
 * Lightweight, zero-dependency SEO & JSON-LD Manager for React 19
 */
export default function SEO({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = DEFAULT_IMAGE,
  keywords,
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  schema,
  breadcrumbs,
}) {
  const location = useLocation();

  useEffect(() => {
    // 1. Document Title
    const finalTitle = title ? (title.includes("Biz360") ? title : `${title} | Biz360 Compliance`) : DEFAULT_TITLE;
    document.title = finalTitle;

    // Helper to set or create meta tag
    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Helper to set or create link tag
    const setLink = (rel, href) => {
      if (!href) return;
      let el = (rel === "canonical" ? document.getElementById("biz360-canonical") : null) || document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (rel === "canonical") el.id = "biz360-canonical";
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // 2. Standard Meta Tags
    const finalDesc = description || DEFAULT_DESC;
    setMeta("name", "description", finalDesc);
    setMeta("name", "robots", robots);
    if (keywords) setMeta("name", "keywords", keywords);

    // 3. Canonical Link
    const cleanPath = (p) => {
      if (!p || p === "/") return "/";
      return p.endsWith("/") ? p.slice(0, -1) : p;
    };

    let finalCanonical;
    if (canonical) {
      if (canonical.startsWith("http")) {
        try {
          const u = new URL(canonical);
          finalCanonical = `${u.origin}${cleanPath(u.pathname)}${u.search}`;
        } catch (e) {
          finalCanonical = canonical;
        }
      } else {
        const p = canonical.startsWith("/") ? canonical : `/${canonical}`;
        finalCanonical = `${BASE_URL}${cleanPath(p)}`;
      }
    } else {
      finalCanonical = `${BASE_URL}${cleanPath(location.pathname)}`;
    }
    setLink("canonical", finalCanonical);

    // 4. Open Graph Tags
    const finalImage = ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDesc);
    setMeta("property", "og:url", finalCanonical);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:image", finalImage);
    setMeta("property", "og:site_name", "Biz360 Compliance");
    setMeta("property", "og:locale", "en_IN");

    // 5. Twitter Card Tags
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDesc);
    setMeta("name", "twitter:image", finalImage);

    // 6. JSON-LD Structured Data
    let scriptEl = document.getElementById("biz360-seo-schema");
    if (!scriptEl) {
      scriptEl = document.createElement("script");
      scriptEl.id = "biz360-seo-schema";
      scriptEl.type = "application/ld+json";
      document.head.appendChild(scriptEl);
    }

    let schemaList = [];
    if (Array.isArray(schema)) {
      schemaList = [...schema];
    } else if (schema && typeof schema === "object") {
      schemaList = [schema];
    }

    // Auto-generate BreadcrumbList schema if breadcrumbs are provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": BASE_URL,
          },
          ...breadcrumbs.map((b, idx) => ({
            "@type": "ListItem",
            "position": idx + 2,
            "name": b.label,
            "item": b.href ? (b.href.startsWith("http") ? b.href : `${BASE_URL}${b.href}`) : undefined,
          })),
        ],
      };
      schemaList.push(breadcrumbSchema);
    }

    if (schemaList.length > 0) {
      scriptEl.textContent = JSON.stringify(schemaList.length === 1 ? schemaList[0] : schemaList);
    } else {
      scriptEl.textContent = "";
    }
  }, [title, description, canonical, ogType, ogImage, keywords, robots, schema, breadcrumbs, location.pathname]);

  return null;
}
